import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Delete as DeleteIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import brandingConfig from '../../config/brandingConfig';

const PageContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '900px',
  margin: '0 auto',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1),
}));

const AccountDeletion = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Container>
      <PageContainer elevation={2}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Konto löschen
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
          {brandingConfig.appName} – Kontolöschung beantragen
        </Typography>

        <Divider sx={{ my: 3 }} />

        <SectionTitle variant="h5" component="h2">
          So löschen Sie Ihr Konto
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Wenn Sie Ihr {brandingConfig.appName}-Konto und alle damit verbundenen Daten löschen möchten,
          folgen Sie bitte den nachstehenden Schritten.
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon>
              <EmailIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="1. Löschungsanfrage per E-Mail senden"
              secondary={
                <>
                  Senden Sie eine E-Mail an <strong>{brandingConfig.email}</strong> mit
                  dem Betreff „Konto löschen". Geben Sie die E-Mail-Adresse oder Telefonnummer an,
                  mit der Sie sich registriert haben.
                </>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="2. Identitätsprüfung"
              secondary="Wir verifizieren Ihre Identität, um sicherzustellen, dass nur Sie selbst die Löschung Ihres Kontos veranlassen können."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <DeleteIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="3. Löschung wird durchgeführt"
              secondary="Nach erfolgreicher Verifizierung wird Ihr Konto innerhalb von 30 Tagen vollständig gelöscht."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <SectionTitle variant="h5" component="h2">
          Welche Daten werden gelöscht?
        </SectionTitle>
        <List dense>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Benutzerprofil (Name, E-Mail, Telefonnummer, Profilbild)" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Fahrthistorie und Buchungsdaten" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Standortdaten und Adressfavoriten" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Bewertungen und Feedback" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Push-Benachrichtigungs-Token" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Treuekarten-Daten und Stempelkarten" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <SectionTitle variant="h5" component="h2">
          Welche Daten werden aufbewahrt?
        </SectionTitle>
        <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <WarningIcon color="warning" sx={{ mt: 0.5 }} />
            <Typography variant="body2">
              Bestimmte Daten müssen aufgrund gesetzlicher Aufbewahrungspflichten auch nach der Kontolöschung
              für den vorgeschriebenen Zeitraum gespeichert werden. Diese Daten werden anonymisiert und sind
              nicht mehr Ihrem Konto zugeordnet.
            </Typography>
          </Box>
        </Box>
        <List dense>
          <ListItem>
            <ListItemIcon><ScheduleIcon color="action" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Rechnungs- und Zahlungsdaten"
              secondary="6 Jahre (§ 257 HGB) bzw. 10 Jahre (§ 147 AO)"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><ScheduleIcon color="action" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Steuerrelevante Belege"
              secondary="10 Jahre (§ 147 AO)"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <SectionTitle variant="h5" component="h2">
          Kontakt
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Bei Fragen zur Kontolöschung oder zum Datenschutz erreichen Sie uns unter:
        </Typography>
        <Typography variant="body1" paragraph>
          {brandingConfig.owner && <>{brandingConfig.owner}<br /></>}
          {brandingConfig.companyName}<br />
          E-Mail: {brandingConfig.email}<br />
          Telefon: {brandingConfig.phone}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="textSecondary">
            {brandingConfig.copyright || `© ${currentYear} ${brandingConfig.companyName}`}
          </Typography>
        </Box>
      </PageContainer>
    </Container>
  );
};

export default AccountDeletion;
